import classNames from 'classnames';
import { isEqual } from 'lodash';

import React, {
  useCallback, useEffect, useId, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import ClearIcon from '@/assets/images/icons/clear.svg';
import ArrowIcon from '@/assets/images/icons/expand_more.svg';
import { useClickOutside } from '@/hooks/useClickOutside';
import useCloseByKey from '@/hooks/useCloseByKey';
import { useInfinityScroll } from '@/hooks/useInfinityScroll';

import FormElement from '../../form/FormElement/FormElement';
import Option from './Option/Option';

import styles from './styles.module.scss';

interface SelectProps {
  list: (string | number | boolean)[];
  name: string;
  id?: string;
  value: string | number | boolean | undefined;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  transformValue?: (elem: any) => string;
  fullWidth?: boolean;
  labelText?: string;
  required?: boolean;
  error?: string;
  small?: boolean;
  disabled?: boolean;
  openUp?: boolean;
  fetchError?: string;
  lngKey?: string;
}

const Select = ({
  list,
  name,
  id,
  value,
  onChange,
  className,
  fullWidth,
  labelText,
  error,
  required,
  small,
  disabled,
  transformValue,
  openUp,
  fetchError,
  lngKey = '',
}: SelectProps) => {
  const { t } = useTranslation();

  const [isActive, setIsActive] = useState(false);
  const [showedOptionsLength, setShowedOptionsLength] = useState(50);
  const [searchText, setSearchText] = useState('');

  const wrappedRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const textFieldRef = useRef<HTMLInputElement>(null);

  const printedValue = (elem: string | number | boolean): string => (
    transformValue ? transformValue(elem) : elem.toString()
  );

  const closeMenu = () => setIsActive(false);

  const clickOption = (e: React.MouseEvent<HTMLLabelElement>) => {
    e.stopPropagation();
  };

  const searchTextHandler = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => setSearchText(e.currentTarget.value),
    [],
  );

  const clearTextField = (e: React.MouseEvent<SVGElement>) => {
    setSearchText('');
    e.stopPropagation();
  };

  const isErrorClass = error && styles.error;
  const isOpenClass = isActive && styles.isOpen;
  const smallClass = small && styles.small;
  const disabledClass = disabled && styles.disabled;
  const openUpClass = openUp && styles.openUp;

  const selectClass = classNames(styles.select, isOpenClass, isErrorClass, className, smallClass, disabledClass);
  const listClass = classNames(styles.list, isOpenClass, openUpClass);
  const valueClass = classNames(styles.value, isErrorClass, smallClass, disabledClass);
  const arrowClass = classNames(styles.icon, styles.arrow, isErrorClass, isOpenClass, smallClass, disabledClass);

  useClickOutside(wrappedRef, closeMenu);
  useCloseByKey(closeMenu);
  useInfinityScroll(listRef, () => setShowedOptionsLength((prev) => prev + 50));

  const formId = id ?? useId();

  const filteredList = useMemo(() => {
    if (!searchText.trim()) return list;
    const lower = searchText.toLowerCase();
    return list.filter((item) => printedValue(item).toLowerCase().includes(lower));
  }, [list, searchText, transformValue]);

  useEffect(() => {
    if (isActive && textFieldRef.current) {
      textFieldRef.current.focus();
    } else {
      setSearchText('');
    }
  }, [isActive]);

  return (
    <FormElement
      required={required}
      fullWidth={fullWidth}
      error={error}
      isActive={isActive}
      hasValue={value !== undefined}
      labelText={labelText || t(lngKey)}
      id={formId || name}
      onClick={() => setIsActive(!isActive)}
      small={small}
      disabled={disabled}
    >
      <div className={selectClass} tabIndex={0} role="button" ref={wrappedRef}>
        {isActive ? (
          <input
            type="text"
            value={searchText}
            onChange={searchTextHandler}
            className={styles.searchText}
            ref={textFieldRef}
          />
        ) : (
          value !== undefined && <span className={valueClass}>{printedValue(value)}</span>
        )}

        <div className={listClass} ref={listRef}>
          {fetchError && (
            <div className={styles.info}>
              Ошибка при запросе данных
              <br />
              {fetchError.includes('403') ? 'Нет доступа' : fetchError}
            </div>
          )}
          {!required && (
            <Option
              name={`${formId}-${name}`}
              type="checkbox"
              id={`${formId}-${name} clear`}
              value=""
              className={styles.reset}
              onClick={clickOption}
              onChange={onChange}
              small={small}
            >
              {t('button.optionsClear')}
            </Option>
          )}
          {filteredList.length ? (
            filteredList.map((option, i) => {
              if (i > showedOptionsLength) return null;
              const isOptionSelected = value !== undefined && value.toString() === option.toString();
              return (
                <Option
                  key={option.toString()}
                  name={`${formId}-${name}`}
                  id={`${formId}-${name}-${option}`}
                  value={option.toString()}
                  onChange={onChange}
                  className={isOptionSelected ? styles.selected : ''}
                  onClick={clickOption}
                  checked={isOptionSelected}
                  small={small}
                >
                  {printedValue(option)}
                </Option>
              );
            })
          ) : (
            <div className={styles.info}>{t('common.no_result')}</div>
          )}
        </div>

        {isActive && searchText ? (
          <ClearIcon className={arrowClass} onClick={clearTextField} />
        ) : (
          <ArrowIcon className={arrowClass} />
        )}
      </div>
    </FormElement>
  );
};

export default React.memo(Select, isEqual);
